<script setup lang="ts">
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { User, Lock } from '@element-plus/icons-vue'
import { adminLogin } from '@/api/modules/auth'
import { useUserStore } from '@/stores/user'
import { loginAsideBrandBackdrop, appBrandLogo } from '@/config/brand-assets'

const router = useRouter()
const route = useRoute()
const user = useUserStore()

const form = ref({ username: '', password: '' })
const loading = ref(false)

async function submit() {
  if (!form.value.username.trim() || !form.value.password) {
    ElMessage.warning('请输入账号与密码')
    return
  }
  loading.value = true
  try {
    const { data } = await adminLogin({
      username: form.value.username.trim(),
      password: form.value.password,
    })
    if (Number(data.status) !== 1 || !data.token) {
      ElMessage.error(data.msg || '登录失败')
      return
    }
    user.setAuth(data.token, data.username || form.value.username.trim())
    const redirect =
      typeof route.query.redirect === 'string' && route.query.redirect.startsWith('/') ? route.query.redirect : '/home'
    await router.replace(redirect)
    ElMessage.success('欢迎回来')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="gk-login">
    <div class="gk-login__shell">
      <aside class="gk-login__aside" :style="{ '--gk-login-brand-bg': `url(${loginAsideBrandBackdrop})` }">
        <p class="gk-login__kicker">ENTERPRISE</p>
        <h1 class="gk-login__aside-title">贷款业务管理控制台</h1>
        <p class="gk-login__aside-sub">会话由服务端签发 JWT，请求头附带 <code>Authorization: Bearer …</code></p>
        <ul class="gk-login__bullets">
          <li>细粒度表单与表格布局</li>
          <li>运营指标与授信流程一体化</li>
        </ul>
      </aside>
      <section class="gk-login__panel">
        <header class="gk-login__panel-top">
          <img :src="appBrandLogo" alt="" class="gk-login__brand-mark" width="48" height="48" />
          <span class="gk-login__badge">SECURE LOGIN</span>
          <h2 class="gk-login__panel-title">账号登录</h2>
          <p class="gk-login__panel-muted">请输入后台管理员凭证</p>
        </header>
        <el-form label-position="top" size="large" @submit.prevent class="gk-login__form">
          <el-form-item label="用户名">
            <el-input v-model="form.username" maxlength="48" autocomplete="username" placeholder="管理员账号" clearable>
              <template #prefix>
                <el-icon><User /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item label="密码">
            <el-input
              v-model="form.password"
              maxlength="72"
              autocomplete="current-password"
              type="password"
              show-password
              placeholder="请输入密码"
              clearable
            >
              <template #prefix>
                <el-icon><Lock /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item class="gk-login__submit-row">
            <el-button :loading="loading" type="primary" class="gk-login__submit" native-type="button" @click="submit">
              进入后台
            </el-button>
          </el-form-item>
        </el-form>
        <el-collapse class="gk-login__collapse">
          <el-collapse-item title="开发与测试提示" name="1">
            <div class="gk-login__hints">
              默认 <code>admin-key</code> 等为开发约定值；账号 <code>root</code>/<code>admin</code> 与数据库哈希需一致，详见服务端
              <code>docs/API.md</code> §2。
            </div>
          </el-collapse-item>
        </el-collapse>
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
.gk-login {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background:
    radial-gradient(ellipse 80% 50% at 50% -20%, rgba(59, 130, 246, 0.45), transparent),
    radial-gradient(ellipse at 85% 20%, rgba(14, 165, 233, 0.2), transparent),
    linear-gradient(145deg, #0f172a 0%, #1e293b 55%, #0f172a 100%);
  padding: clamp(16px, 4vw, 40px);
}

.gk-login__shell {
  width: min(1000px, 100%);
  display: grid;
  grid-template-columns: 1fr minmax(0, 400px);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 40px 100px rgba(15, 23, 42, 0.65);
}

@media (max-width: 900px) {
  .gk-login__shell {
    grid-template-columns: 1fr;
  }

  .gk-login__aside {
    display: none;
  }

  .gk-login__panel {
    padding: clamp(28px, 8vw, 40px);
  }
}

.gk-login__aside {
  background:
    radial-gradient(circle at 50% 20%, rgba(15, 40, 80, 0.35), transparent 52%),
    linear-gradient(
      158deg,
      rgba(23, 37, 84, 0.92) 0%,
      rgba(15, 31, 64, 0.9) 45%,
      rgba(23, 37, 84, 0.94) 100%
    ),
    var(--gk-login-brand-bg, none) center 40% / cover no-repeat,
    linear-gradient(160deg, #1e3a8a 0%, #172554 100%);
  color: #eff6ff;
  padding: 48px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
}

.gk-login__kicker {
  margin: 0;
  letter-spacing: 0.35em;
  font-size: 11px;
  font-weight: 700;
  opacity: 0.75;
}

.gk-login__aside-title {
  margin: 0;
  font-size: clamp(24px, 3vw, 30px);
  font-weight: 800;
  line-height: 1.25;
}

.gk-login__aside-sub {
  margin: 0;
  opacity: 0.82;
  font-size: 14px;
  line-height: 1.65;
}

.gk-login__bullets {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.85;
  opacity: 0.88;
}

.gk-login__panel {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  padding: 44px 40px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gk-login__panel-top {
  margin-bottom: 8px;
  text-align: left;
}

.gk-login__brand-mark {
  display: block;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  object-fit: cover;
  margin-bottom: 14px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.15);
}

.gk-login__badge {
  display: inline-block;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--el-color-primary);
  font-size: 11px;
  margin-bottom: 8px;
}

.gk-login__panel-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--ep-text-strong);
}

.gk-login__panel-muted {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--ep-text-muted);
}

.gk-login__submit-row {
  margin-top: 8px !important;
}

.gk-login__submit {
  width: 100%;
  height: 46px !important;
  font-size: 15px !important;
  border-radius: 10px !important;
}

.gk-login__hints {
  font-size: 12px;
  line-height: 1.65;
  color: var(--el-text-color-regular);
}

.gk-login__collapse {
  margin-top: 12px;
  border: none;
  --el-collapse-border-color: transparent;
}

.gk-login__collapse :deep(.el-collapse-item__header) {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.gk-login__collapse :deep(.el-collapse-item__wrap) {
  border-bottom: none;
}
</style>
